import { useEffect, useRef, useState } from "react";
import cx from "classnames";

import { useHideOnScrollContext } from "components/HideOnScroll/HideOnScrollContext";
import { RUN_STATES } from "components/Run/elements/StateBadge";
import { Cross } from "components/icons/generated";
import Badge from "ds/components/Badge";
import { BadgeVariants } from "ds/components/Badge/types";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import Icon from "ds/components/Icon";
import { IconColors } from "ds/components/Icon/types";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import { TypographyColors } from "types/Colors";
import { RunObservabilitySeverity, RunState } from "types/generated";
import { IconComponent } from "types/Icon";

import { getDurationLabel, getPhaseLabel } from "../../utils";
import RunObservabilityErrorCard from "../../ErrorCard";
import { PHASE_EXECUTION_TIME_GRID_TEMPLATE } from "../constants";
import styles from "./styles.module.css";
import { PhaseExecutionTimeBar } from "../types";

type PhaseExecutionTimeSeverityConfig = {
  barClassName: string;
  badge?: { variant: BadgeVariants; text?: string };
  icon?: IconComponent;
  iconClassName?: string;
  iconColor?: IconColors;
  textColor?: TypographyColors;
};

const PHASE_EXECUTION_TIME_SEVERITIES: Record<
  RunObservabilitySeverity,
  PhaseExecutionTimeSeverityConfig
> = {
  [RunObservabilitySeverity.None]: {
    barClassName: styles.barOk,
    badge: { variant: "green", text: "OK" },
  },
  [RunObservabilitySeverity.Minor]: {
    barClassName: styles.barWarning,
    badge: { variant: "yellow" },
  },
  [RunObservabilitySeverity.Major]: {
    barClassName: styles.barDanger,
    badge: { variant: "red" },
  },
};

const PHASE_EXECUTION_TIME_FAILED: PhaseExecutionTimeSeverityConfig = {
  barClassName: styles.barDanger,
  badge: { variant: "red", text: "Failed" },
  icon: Cross,
  iconClassName: styles.phaseIconDanger,
  iconColor: "danger",
  textColor: "danger",
};

const PhaseExecutionTimeRow = ({
  state: {
    runState,
    failed,
    errors,
    duration: { p95Seconds, p95Severity },
  },
  seconds,
  offsetPercent,
  widthPercent,
}: PhaseExecutionTimeBar) => {
  const severityConfig = p95Severity ? PHASE_EXECUTION_TIME_SEVERITIES[p95Severity] : undefined;
  const config = failed ? PHASE_EXECUTION_TIME_FAILED : severityConfig;

  const badge = config?.badge;
  const label = getPhaseLabel(runState);
  const delta = p95Seconds ? getDurationLabel(seconds - p95Seconds) : undefined;
  const badgeText = badge && (badge.text ?? (delta && `+${delta}`));
  const phaseErrors = failed ? errors : [];
  const errorCount = phaseErrors.length;
  const stripRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  const { scrollableElement } = useHideOnScrollContext();
  const hasErrors = errorCount > 0;

  useEffect(() => {
    const strip = stripRef.current;

    if (!strip || !hasErrors) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const rootTop = entry.rootBounds?.top ?? 0;
        setIsStuck(entry.intersectionRatio < 1 && entry.boundingClientRect.top <= rootTop);
      },
      { root: scrollableElement ?? null, rootMargin: "-1px 0px 0px 0px", threshold: 1 }
    );

    observer.observe(strip);

    return () => observer.disconnect();
  }, [scrollableElement, hasErrors]);

  return (
    <Box direction="column" className={styles.row}>
      <Box
        grid
        gridTemplate={PHASE_EXECUTION_TIME_GRID_TEMPLATE}
        gap="x-large"
        padding="0 x-large"
        align="center"
        className={cx(styles.strip, isStuck && styles.stuck)}
        ref={stripRef}
      >
        <Box align="center" justify="between" gap="medium" padding="0 x-large 0 0">
          <Box align="center" gap="medium" zeroMinWidth>
            <Box
              align="center"
              justify="center"
              shrink="0"
              className={cx(styles.phaseIcon, config?.iconClassName)}
            >
              <Icon
                src={
                  config?.icon ?? RUN_STATES[runState]?.icon ?? RUN_STATES[RunState.Unknown].icon
                }
                color={config?.iconColor}
              />
            </Box>
            <TextEllipsis tooltip={label}>
              {(ellipsisProps) => (
                <Typography
                  {...ellipsisProps}
                  tag="span"
                  variant="p-body4"
                  color={config?.textColor}
                >
                  {label}
                </Typography>
              )}
            </TextEllipsis>
            {hasErrors && <Counter count={errorCount} variant="danger" size="small" />}
          </Box>

          {badge && badgeText && <Badge type="weak" variant={badge.variant} text={badgeText} />}
        </Box>

        <Box align="center" gap="small" className={styles.track}>
          <div
            className={cx(styles.bar, config?.barClassName)}
            style={{ marginLeft: `${offsetPercent}%`, width: `${widthPercent}%` }}
          />
          <Typography tag="span" variant="p-body3" color="secondary" className={styles.duration}>
            {getDurationLabel(seconds)}
          </Typography>
        </Box>
      </Box>

      {hasErrors && (
        <Box direction="column" gap="medium" padding="0 x-large x-large" className={styles.errors}>
          {phaseErrors.map((error, index) => (
            <RunObservabilityErrorCard
              key={`${error.resourceAddress}:${error.message}`}
              error={error}
              ariaLabel={
                errorCount > 1 ? `${label} error ${index + 1} of ${errorCount}` : `${label} error`
              }
              initialIsCollapsed={index > 0}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PhaseExecutionTimeRow;
